import Globe from "images/global/countries/globe.svg"
import { eases } from "library/eases"
import { fresponsive } from "library/fullyResponsive"
import { useCallback, useEffect, useState } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import { countries, countriesOptions } from "utils/countries"
import i18next from "../../i18n"

const LanguageSwitcher = () => {
	const [open, setOpen] = useState(false)
	const [value, setValue] = useState<string>()

	useEffect(() => {
		const lng = localStorage.getItem("lng")
		if (lng) setValue(lng)
	}, [])

	const handleValueChange = useCallback((newValue: string) => {
		setValue(newValue)
		i18next.changeLanguage(newValue)
		localStorage.setItem("lng", newValue)
		setOpen(false)
	}, [])

	return (
		<Wrapper>
			<Content data-state={open ? "open" : "closed"}>
				{countriesOptions.map((option) => (
					<Item
						key={option.value}
						onClick={() => handleValueChange(option.value)}
					>
						<ItemImage src={option.image} alt={option.label} />
						<div>{option.label}</div>
					</Item>
				))}
			</Content>
			<Trigger
				onClick={() => setOpen((prev) => !prev)}
				data-state={open ? "open" : "closed"}
			>
				<Item>
					{value && value in countries ? (
						<>
							<ItemImage
								src={countries[value as keyof typeof countries].image}
								alt="country"
							/>
							<div>{countries[value as keyof typeof countries].label}</div>
						</>
					) : (
						<>
							<ItemImage src={Globe} alt="globe" />
							<div>Language</div>
						</>
					)}
				</Item>
			</Trigger>
		</Wrapper>
	)
}

const Wrapper = styled.div`
	position: relative;
	z-index: 10;

	${fresponsive(css`
		height: 36px;
		width: 135px;
	`)}

	& * {
		font-family: Matter, sans-serif;
	}
`

const Trigger = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	cursor: pointer;
	height: 100%;
	position: relative;
  	z-index: 2;
	background: #3B3B3B;
	box-shadow:
		0 1.11px 2.23px 0 #13090014,
		0 -0.56px 0.28px 0 #0000001F inset,
		0 0.56px 0.28px 0 #FFFFFF80 inset,
		0 1.11px 7.68px 0 #FFFFFF0A;

	${fresponsive(css`
		padding: 0 10px;
		border-radius: 25px;
	`)}

	&:focus {
		outline: none;
	}

	&[data-state="open"]::after {
		transform: translateY(-50%) rotate(45deg);
	}

	&::after {
		position: absolute;
		right: 13px;
		top: 50%;
		transform: translateY(-30%) rotate(-135deg);
		content: '';
		border: solid white;
		border-width: 0 1px 1px 0;
		display: inline-block;
		padding: 4px;
		width: 8px;
		height: 8px;
		transition: transform 0.5s ease, -webkit-transform 0.5s ease;
	}
`

const Content = styled.div`
	background: black;
	position: absolute;
	z-index: 1;
	transition: transform 0.25s ${eases.quad.out}, opacity 0.25s ${eases.quad.out};
	transform-origin: top center;
	transform: translateY(0);
	opacity: 0;
	pointer-events: none;

	&[data-state="open"] {
		transform: translateY(-10px);
		opacity: 1;
		pointer-events: auto;
	}

	${fresponsive(css`
		width: 135px;
		left: 0;
		background: black;
		border-radius: 20px 20px 0 0;
		box-shadow: 0 4px 12px rgb(0 0 0 / 40%);
		padding: 10px 10px 30px;
		top: -60px;

		& > :not(:last-child) {
			margin-bottom: 10px;
		}
	`)}
`

const Item = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	color: ${colors.pureWhite};
	cursor: pointer;

	${fresponsive(css`
		font-size: 12px;
		letter-spacing: -0.24px;
		font-weight: 500;
		line-height: 128%;
		padding: 0;
	`)}

	* {
		font-family: Matter, sans-serif;
	}
`

const ItemImage = styled.img`
	${fresponsive(css`
		width: 20px;
	`)}
`

export default LanguageSwitcher
